// react
import React from 'react';
import styled from 'styled-components';

// react-aria
import { useLabel } from 'react-aria';

// components
import Tooltip from '@/components/atoms/Tooltip/Tooltip';
import Label from '@/components/atoms/Label/Label';
import SupportingText from '@/components/atoms/SupportingText/SupportingText';
import { Icon } from '@/components/atoms/Icon/Icon';

/**
 * BaseField is a wrapper for a form field.
 */
export const BaseField = ({
  id = 'form-field',
  ariaLabel,
  label,
  tooltip,
  description,
  isRequired,
  isHideTitle,
  children,
  className,
  ...props
}: BaseFieldProps) => {
  // hooks/
  const { labelProps } = useLabel({
    ...props,
    id,
    'aria-label': ariaLabel || label,
  });

  return (
    <Wrapper className={className}>
      <Header>
        {label && !isHideTitle && (
          <StyledLabel
            id={id}
            text={label}
            {...labelProps}
            isRequired={isRequired}
            aria-labelledby={id}
          />
        )}
        {tooltip && !isHideTitle && (
          <Tooltip content={tooltip}>
            <TooltipIcon>
              <Icon icon='question-circle' fontSize={16} />
            </TooltipIcon>
          </Tooltip>
        )}
      </Header>
      {children}
      {description && <StyledSupportingText text={description} />}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${(props) => props.theme.spacing[4]};
`;

const StyledSupportingText = styled(SupportingText)`
  margin-top: ${(props) => props.theme.spacing[4]};
`;

const TooltipIcon = styled.div`
  color: ${(props) => props.theme.color.neutral[70]};
`;

export interface FieldProps<T> {
  id?: string;
  ariaLabel?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  tooltip?: string;
  value?: T;
  onChange?: (value: T) => void;
  validate?: (value?: T) => boolean;
  isRequired?: boolean;
  isHideTitle?: boolean;
  isDisabled?: boolean;
}

export interface BaseFieldProps {
  id?: string;
  ariaLabel?: string;
  label?: string;
  tooltip?: string;
  description?: string;
  isRequired?: boolean;
  isHideTitle?: boolean;
  isDisabled?: boolean;
  children?: React.ReactNode;
  validate?: (value?: string | boolean) => boolean;
  className?: string;
}

BaseField.componentName = 'BaseField';

export default BaseField;
